import { ShieldAlert } from "lucide-react";

const LOGO_URL = "/logo192.png";

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-slate-950 text-slate-300">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-slate-950/90 backdrop-blur-md border-b border-slate-800/50">
        <div className="max-w-3xl mx-auto flex items-center gap-3 px-6 py-4">
          <div className="h-8 w-8 rounded-lg overflow-hidden bg-slate-900 ring-1 ring-white/10">
            <img src={LOGO_URL} alt="DMM" className="h-full w-full object-cover" style={{ mixBlendMode: "screen" }} />
          </div>
          <span className="text-lg font-bold text-white tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
            Callintell<span className="text-blue-400">&trade;</span>
          </span>
          <span className="text-xs text-slate-600 ml-1">Legal</span>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-10 space-y-10">
        {/* Title */}
        <div className="text-center space-y-2">
          <div className="inline-flex items-center justify-center h-12 w-12 rounded-xl bg-blue-500/10 border border-blue-500/20 mb-3">
            <ShieldAlert className="h-6 w-6 text-blue-400" />
          </div>
          <h1 className="text-3xl font-bold text-white" style={{ fontFamily: "Outfit, sans-serif" }}>
            Terms & Conditions
          </h1>
          <p className="text-sm text-slate-500">Callintell by Digital Master Media LLC</p>
        </div>

        {/* Agreement Content */}
        <div className="space-y-8 text-sm leading-relaxed">
          <Section title={null}>
            <p>Welcome to Callintell, operated by Digital Master Media LLC.</p>
            <p className="font-medium text-slate-200 mt-3">Company Details:</p>
            <ul>
              <li>CEO: Abid Ali</li>
              <li>Email: abidali@digitalmastermedia.com</li>
              <li>Address: 30 N Gould St, Ste R, Sheridan, WY 82801, USA</li>
            </ul>
            <p className="mt-3">By using this platform, you agree to all terms below.</p>
          </Section>

          <Section title="1. Service Description">
            <p>Callintell provides AI-based call tracking, monitoring, filtering, and analytics.</p>
            <ul>
              <li>AI outputs are not guaranteed accurate</li>
              <li>The platform is a decision-support tool only</li>
            </ul>
          </Section>

          <Section title="2. No Liability">
            <p>You agree that:</p>
            <ul>
              <li>We are NOT liable for any loss of money, business, leads, or reputation</li>
              <li>We are NOT responsible for wrong AI decisions</li>
              <li>We are NOT responsible for fake or low-quality calls</li>
              <li>We are NOT liable for system errors, downtime, or bugs</li>
            </ul>
            <p className="mt-2 font-medium text-slate-200">You use this software at your own risk.</p>
          </Section>

          <Section title="3. Data Loss">
            <ul>
              <li>We do NOT guarantee data storage or recovery</li>
              <li>Data may be lost due to server failure, bugs, or API issues</li>
              <li>Users must maintain their own backups</li>
            </ul>
          </Section>

          <Section title="4. AI Disclaimer">
            <ul>
              <li>AI may produce incorrect results</li>
              <li>AI decisions are probabilistic</li>
              <li>False detection may occur</li>
            </ul>
            <p className="mt-2">Users should not rely solely on AI for decisions.</p>
          </Section>

          <Section title="5. Third-Party Services">
            <ul>
              <li>We use external APIs and services</li>
              <li>We are not responsible for their performance or accuracy</li>
            </ul>
          </Section>

          <Section title="6. Payments">
            <ul>
              <li>All payments are NON-REFUNDABLE</li>
              <li>No chargebacks allowed</li>
              <li>Minutes cannot be withdrawn</li>
              <li>Pricing may change anytime</li>
            </ul>
          </Section>

          <Section title="7. Chargebacks">
            <ul>
              <li>Account will be permanently banned</li>
              <li>Legal action may be taken</li>
            </ul>
          </Section>

          <Section title="8. Traffic Responsibility">
            <ul>
              <li>We do not guarantee call quality</li>
              <li>Users are responsible for their traffic</li>
            </ul>
          </Section>

          <Section title="9. Earnings Disclaimer">
            <ul>
              <li>No guarantee of ROI or performance</li>
              <li>We are not responsible for advertiser payouts</li>
            </ul>
          </Section>

          <Section title="10. Account Responsibility">
            <ul><li>Users are responsible for login credentials and activity</li></ul>
          </Section>

          <Section title="11. Downtime">
            <ul><li>No guarantee of uptime or error-free service</li></ul>
          </Section>

          <Section title="12. Limitation of Liability">
            <p>We are not liable for:</p>
            <ul>
              <li>Loss of profits</li>
              <li>Data loss</li>
              <li>Indirect damages</li>
            </ul>
          </Section>

          <Section title="13. Indemnification">
            <p>Users agree to protect Digital Master Media LLC from any claims or damages.</p>
          </Section>

          <Section title="14. Termination">
            <p>We may suspend or terminate accounts at any time without notice.</p>
          </Section>

          <Section title="15. Jurisdiction">
            <p>Governed by laws of Wyoming, USA.</p>
          </Section>

          <Section title="16. Updates">
            <p>Terms may be updated anytime.</p>
          </Section>

          {/* Divider */}
          <hr className="border-slate-800" />

          <Section title="Privacy Policy" id="privacy">
            <p>We collect:</p>
            <ul>
              <li>Name, email</li>
              <li>Call data (recordings, logs)</li>
              <li>Usage activity</li>
            </ul>
            <p className="mt-3">We use data to:</p>
            <ul>
              <li>Provide analytics</li>
              <li>Improve AI</li>
              <li>Detect fraud</li>
            </ul>
            <p className="mt-3">We do not guarantee data security or accuracy.</p>
            <p>Users agree that data may be processed by third-party services and stored globally.</p>
          </Section>

          <Section title="Refund Policy">
            <ul>
              <li>All sales are final</li>
              <li>No refunds under any condition</li>
              <li>No exceptions for poor results, AI errors, or unused balance</li>
            </ul>
          </Section>

          <Section title="Traffic Policy">
            <p>Users must NOT:</p>
            <ul>
              <li>Send fake calls</li>
              <li>Use bots</li>
              <li>Send incentivized traffic</li>
              <li>Manipulate call duration</li>
            </ul>
            <p className="mt-3">If detected:</p>
            <ul>
              <li>Account will be banned</li>
              <li>Balance will be forfeited</li>
            </ul>
          </Section>
        </div>

        {/* Footer */}
        <div className="text-center pt-6 border-t border-slate-800/50">
          <p className="text-xs text-slate-600">
            Callintell&trade; by Digital Master Media LLC &mdash; 30 N Gould St, Ste R, Sheridan, WY 82801, USA
          </p>
        </div>
      </div>
    </div>
  );
}

function Section({ title, id, children }) {
  return (
    <div id={id}>
      {title && (
        <h2 className="text-lg font-semibold text-slate-100 mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>
          {title}
        </h2>
      )}
      <div className="text-slate-400 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1 [&_ul]:mt-1.5 [&_li]:text-slate-400">
        {children}
      </div>
    </div>
  );
}
